import { useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { ColorModeContext } from './color-mode.js';
import { buildTheme } from './theme.js';

const COLOR_MODE_STORAGE_KEY = 'blade-color-mode';
const getPreferredSystemMode = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

const getStoredColorMode = () => {
  if (typeof window === 'undefined') {
    return 'system';
  }

  const storedMode = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  return storedMode === 'light' || storedMode === 'dark' || storedMode === 'system'
    ? storedMode
    : 'system';
};

export default function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(getStoredColorMode);
  const [systemMode, setSystemMode] = useState(getPreferredSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleChange = (event) => {
      setSystemMode(event.matches ? 'light' : 'dark');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const resolvedMode = mode === 'system' ? systemMode : mode;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    document.documentElement.setAttribute('data-color-mode', resolvedMode);
  }, [mode, resolvedMode]);

  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);
  const colorModeValue = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
    }),
    [mode, resolvedMode],
  );

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
